import { useMemo, useState } from 'react';
import { PackagePlus, CalendarClock } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney, expiryStatus, type ExpiryBand } from '@nako/core';
import type { Business, Profile, PharmacyBatch, PharmacyDrug } from '@nako/types';
import { isControlledSchedule } from '@nako/types';
import { receiveBatch } from '@/lib/pharmacy-postings';
import type { PharmacyData } from '@/pages/Pharmacy';

const BAND_TONE: Record<ExpiryBand, 'red' | 'accent' | 'neutral' | 'green'> = {
  expired: 'red',
  expiring_90: 'accent',
  expiring_180: 'neutral',
  ok: 'green',
};
const BAND_LABEL: Record<ExpiryBand, string> = {
  expired: 'Expired',
  expiring_90: '≤90 days',
  expiring_180: '≤180 days',
  ok: 'OK',
};

interface ReceiveDraft {
  drugId: string;
  batchNo: string;
  expiryDate: string;
  qty: string;
  unitCost: string;
  onAccount: boolean;
  reference: string;
}

export function BatchesExpiry({
  business,
  profile,
  data,
}: {
  business: Business;
  profile: Profile | null;
  data: PharmacyData;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<ReceiveDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const drugById = useMemo(() => {
    const m = new Map<string, PharmacyDrug>();
    for (const d of data.drugs) m.set(d.id, d);
    return m;
  }, [data.drugs]);

  // Flatten batches, sorted FEFO (soonest expiry first), with a live band.
  const rows = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const all: Array<{ batch: PharmacyBatch; drug: PharmacyDrug | undefined; band: ExpiryBand }> =
      [];
    for (const [drugId, batches] of data.batchesByDrug) {
      for (const b of batches) {
        all.push({
          batch: b,
          drug: drugById.get(drugId),
          band: expiryStatus(b.expiry_date, today).band,
        });
      }
    }
    return all.sort((a, b) => (a.batch.expiry_date < b.batch.expiry_date ? -1 : 1));
  }, [data.batchesByDrug, drugById]);

  async function receive() {
    if (!draft) return;
    const drug = drugById.get(draft.drugId);
    if (!drug) {
      toast.show('Pick a drug.', { tone: 'warning' });
      return;
    }
    if (!draft.batchNo.trim() || !draft.expiryDate || !Number(draft.qty)) {
      toast.show('Batch no, expiry and quantity are required.', { tone: 'warning' });
      return;
    }
    setSaving(true);
    const res = await receiveBatch({
      businessId: business.id,
      userId: profile?.id ?? null,
      drug,
      batchNo: draft.batchNo.trim(),
      expiryDate: draft.expiryDate,
      qty: Number(draft.qty),
      unitCost: Number(draft.unitCost) || 0,
      supplierId: null,
      onAccount: draft.onAccount,
      reference: draft.reference.trim() || undefined,
    });
    setSaving(false);
    if (!res.ok) {
      toast.show(res.error ?? 'Could not receive stock.', { tone: 'error' });
      return;
    }
    toast.show('Stock received.', { tone: 'success' });
    setDraft(null);
    await data.reload();
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-muted flex items-center gap-1.5 text-sm">
          <CalendarClock size={16} /> {rows.length} batches
        </div>
        <Button
          onClick={() =>
            setDraft({
              drugId: data.drugs[0]?.id ?? '',
              batchNo: '',
              expiryDate: '',
              qty: '',
              unitCost: '',
              onAccount: false,
              reference: '',
            })
          }
          disabled={data.drugs.length === 0}
        >
          <PackagePlus size={16} /> Receive stock
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-muted py-10 text-center text-sm">
          No batches yet. Add drugs in the Formulary tab, then receive stock here.
        </p>
      ) : (
        <div className="grid gap-2">
          {rows.map(({ batch, drug, band }) => (
            <Card key={batch.id} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-ink truncate font-medium">{drug?.name ?? 'Unknown'}</span>
                  <span className="text-muted text-xs">#{batch.batch_no}</span>
                  {drug && isControlledSchedule(drug.schedule) && (
                    <Pill tone="red">{drug.schedule}</Pill>
                  )}
                </div>
                <div className="text-muted mt-0.5 text-xs">
                  Exp {batch.expiry_date} · {batch.qty_on_hand} on hand ·{' '}
                  {formatMoney(batch.unit_cost)}/unit
                </div>
              </div>
              <Pill tone={BAND_TONE[band]}>{BAND_LABEL[band]}</Pill>
            </Card>
          ))}
        </div>
      )}

      <Modal open={draft !== null} onClose={() => setDraft(null)} title="Receive stock">
        {draft && (
          <div className="grid gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-ink font-medium">Drug</span>
              <select
                className="rounded-btn border-line h-10 border px-2"
                value={draft.drugId}
                onChange={(e) => setDraft({ ...draft, drugId: e.target.value })}
              >
                {data.drugs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.strength ?? ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Batch / lot no"
                value={draft.batchNo}
                onChange={(e) => setDraft({ ...draft, batchNo: e.target.value })}
              />
              <Input
                label="Expiry date"
                type="date"
                value={draft.expiryDate}
                onChange={(e) => setDraft({ ...draft, expiryDate: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Quantity"
                numeric
                value={draft.qty}
                onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
              />
              <Input
                label="Unit cost"
                numeric
                value={draft.unitCost}
                onChange={(e) => setDraft({ ...draft, unitCost: e.target.value })}
              />
            </div>
            <Input
              label="Supplier invoice ref (optional)"
              value={draft.reference}
              onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            />
            <label className="text-muted flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.onAccount}
                onChange={(e) => setDraft({ ...draft, onAccount: e.target.checked })}
              />
              Bought on account (credit) — otherwise paid cash
            </label>
            <Button onClick={() => void receive()} disabled={saving}>
              {saving ? 'Receiving…' : 'Receive & post to ledger'}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
